// src/components/admin/PromoCodeCreationForm.tsx
import React, { useState } from 'react';
import { Calendar, AlertCircle, Check, RefreshCw, X } from 'lucide-react';
import { adminApi } from '../../services/adminApi';

interface PromoCodeCreationFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  customCode: string;
  planType: 'medium' | 'premium';
  durationType: 'once' | 'repeating' | 'forever';
  durationMonths: number | null;
  expiresAt: string | null;
  maxRedemptions: number | null;
  discountPercentage: number | null;
  description: string;
}

const PromoCodeCreationForm: React.FC<PromoCodeCreationFormProps> = ({
  onSuccess,
  onCancel
}) => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    customCode: '',
    planType: 'medium',
    durationType: 'once',
    durationMonths: 1,
    expiresAt: null,
    maxRedemptions: null,
    discountPercentage: null,
    description: ''
  });
  
  // Form UI state
  const [useCustomCode, setUseCustomCode] = useState(false);
  const [useExpiration, setUseExpiration] = useState(false);
  const [useMaxRedemptions, setUseMaxRedemptions] = useState(false);
  const [useDiscount, setUseDiscount] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<boolean>(false);
  
  // Generate a random code preview if not using custom code
  const [generatedCode, setGeneratedCode] = useState('');
  
  // Generate a random promo code
  const generateRandomCode = () => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setGeneratedCode(result);
  };
  
  // Call code generator on mount
  React.useEffect(() => {
    generateRandomCode();
  }, []);
  
  // Form validations
  const validate = (): boolean => {
    // Clear previous errors
    setError(null);
    
    // Validate custom code if selected
    if (useCustomCode && !formData.customCode.trim()) {
      setError('Custom code is required');
      return false;
    }
    
    // Validate plan type
    if (!['medium', 'premium'].includes(formData.planType)) {
      setError('Valid plan type is required');
      return false;
    }
    
    // Validate duration type
    if (!['once', 'repeating', 'forever'].includes(formData.durationType)) {
      setError('Valid duration type is required');
      return false;
    }
    
    // Duration months is required for once and repeating
    if (['once', 'repeating'].includes(formData.durationType) && !formData.durationMonths) {
      setError('Duration months is required for one-time or recurring codes');
      return false;
    }
    
    // Validate expiration date if enabled
    if (useExpiration && !formData.expiresAt) {
      setError('Expiration date is required when enabled');
      return false;
    }
    
    // Validate max redemptions if enabled
    if (useMaxRedemptions && 
        (formData.maxRedemptions === null || formData.maxRedemptions <= 0)) {
      setError('Maximum redemptions must be a positive number');
      return false;
    }
    
    // Validate discount percentage if enabled
    if (useDiscount && 
        (formData.discountPercentage === null || 
         formData.discountPercentage <= 0 || 
         formData.discountPercentage > 100)) {
      setError('Discount percentage must be between 1 and 100');
      return false;
    }
    
    return true;
  };
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Handle numeric inputs
    if (type === 'number') {
      setFormData({
        ...formData,
        [name]: value ? Number(value) : null
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!validate()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Prepare data for API
      const apiData = {
        ...formData,
        // Use generated code or custom code based on user selection
        customCode: useCustomCode ? formData.customCode : generatedCode,
        // Only include fields that are enabled
        expiresAt: useExpiration ? formData.expiresAt : null,
        maxRedemptions: useMaxRedemptions ? formData.maxRedemptions : null,
        discountPercentage: useDiscount ? formData.discountPercentage : null
      };
      
      // Submit to API
      await adminApi.createPromoCode(apiData);
      
      // Call success callback
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create promo code');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Toggle preview mode
  const handleTogglePreview = () => {
    if (!preview) {
      validate(); // Validate before showing preview
    }
    setPreview(!preview);
  };
  
  // Render code preview or full form
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Create Promo Code</h2>
        <div className="space-x-2">
          <button
            type="button"
            onClick={handleTogglePreview}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200"
          >
            {preview ? 'Edit' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}
      
      {preview ? (
        // Preview mode
        <div className="bg-gray-50 border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Code Preview</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Code Details</h4>
              <div className="bg-white p-4 rounded-lg border shadow-sm">
                <p className="text-2xl font-mono font-bold mb-3 text-blue-600">
                  {useCustomCode ? formData.customCode : generatedCode}
                </p>
                <p className="text-sm text-gray-500 mb-1">
                  <span className="font-medium">Plan:</span> {formData.planType}
                </p>
                <p className="text-sm text-gray-500 mb-1">
                  <span className="font-medium">Duration:</span> {formData.durationType}
                  {formData.durationType !== 'forever' && formData.durationMonths && 
                   ` (${formData.durationMonths} months)`}
                </p>
                {useExpiration && formData.expiresAt && (
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-medium">Expires:</span> {new Date(formData.expiresAt).toLocaleDateString()}
                  </p>
                )}
                {useMaxRedemptions && formData.maxRedemptions && (
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-medium">Max redemptions:</span> {formData.maxRedemptions}
                  </p>
                )}
                {useDiscount && formData.discountPercentage && (
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-medium">Discount:</span> {formData.discountPercentage}%
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Description</h4>
              <div className="bg-white p-4 rounded-lg border shadow-sm">
                {formData.description ? (
                  <p className="text-gray-700">{formData.description}</p>
                ) : (
                  <p className="text-gray-400 italic">No description provided</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleSubmit}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Confirm & Create'}
            </button>
          </div>
        </div>
      ) : (
        // Edit mode (form)
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Code Generation Options */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code Generation
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="useGeneratedCode"
                    name="codeType"
                    checked={!useCustomCode}
                    onChange={() => setUseCustomCode(false)}
                    className="h-4 w-4 text-brand-primary border-gray-300 focus:ring-brand-primary rounded"
                  />
                  <label htmlFor="useGeneratedCode" className="ml-2 block text-sm font-medium text-gray-700">
                    Generate random code
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="useCustomCode"
                    name="codeType"
                    checked={useCustomCode}
                    onChange={() => setUseCustomCode(true)}
                    className="h-4 w-4 text-brand-primary border-gray-300 focus:ring-brand-primary rounded"
                  />
                  <label htmlFor="useCustomCode" className="ml-2 block text-sm font-medium text-gray-700">
                    Use custom code
                  </label>
                </div>
              </div>
            </div>
            
            {/* Code Display/Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {useCustomCode ? 'Custom Code' : 'Generated Code'}
              </label>
              {useCustomCode ? (
                <input
                  type="text"
                  name="customCode"
                  value={formData.customCode}
                  onChange={handleChange}
                  placeholder="Enter custom code (e.g., SPRING2025)"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                  required
                />
              ) : (
                <div className="flex items-center">
                  <span className="p-2 bg-gray-100 border border-gray-300 rounded-md font-mono text-gray-700 mr-2">
                    {generatedCode}
                  </span>
                  <button
                    type="button"
                    onClick={generateRandomCode}
                    className="p-2 text-blue-600 hover:text-blue-800"
                    title="Generate new code"
                  >
                    <RefreshCw className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
            
            {/* Plan Type */}
            <div>
              <label htmlFor="planType" className="block text-sm font-medium text-gray-700 mb-1">
                Plan Type
              </label>
              <select
                id="planType"
                name="planType"
                value={formData.planType}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                required
              >
                <option value="medium">Medium</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            
            {/* Duration Type */}
            <div>
              <label htmlFor="durationType" className="block text-sm font-medium text-gray-700 mb-1">
                Duration Type
              </label>
              <select
                id="durationType"
                name="durationType"
                value={formData.durationType}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                required
              >
                <option value="once">One-time</option>
                <option value="repeating">Recurring</option>
                <option value="forever">Permanent</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {formData.durationType === 'once' && 'One-time: Valid for a specific duration, then expires.'}
                {formData.durationType === 'repeating' && 'Recurring: Auto-renews for the specified duration.'}
                {formData.durationType === 'forever' && 'Permanent: Never expires until manually deactivated.'}
              </p>
            </div>
            
            {/* Duration Months - Only for once and repeating */}
            {formData.durationType !== 'forever' && (
              <div>
                <label htmlFor="durationMonths" className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (Months)
                </label>
                <input
                  type="number"
                  id="durationMonths"
                  name="durationMonths"
                  value={formData.durationMonths || ''}
                  onChange={handleChange}
                  min="1"
                  max="60"
                  placeholder="Enter duration in months"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                  required
                />
              </div>
            )}
            
            {/* Expiration Date - Optional */}
            <div>
              <div className="flex items-center mb-1">
                <input
                  type="checkbox"
                  id="useExpiration"
                  checked={useExpiration}
                  onChange={() => setUseExpiration(!useExpiration)}
                  className="h-4 w-4 text-brand-primary border-gray-300 focus:ring-brand-primary rounded"
                />
                <label htmlFor="useExpiration" className="ml-2 block text-sm font-medium text-gray-700">
                  Set Expiration Date
                </label>
              </div>
              {useExpiration && (
                <div className="flex items-center">
                  <input
                    type="date"
                    id="expiresAt"
                    name="expiresAt"
                    value={formData.expiresAt || ''}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]} // Min date is today
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                    required
                  />
                  <Calendar className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" />
                </div>
              )}
            </div>
            
            {/* Max Redemptions - Optional */}
            <div>
              <div className="flex items-center mb-1">
                <input
                  type="checkbox"
                  id="useMaxRedemptions"
                  checked={useMaxRedemptions}
                  onChange={() => setUseMaxRedemptions(!useMaxRedemptions)}
                  className="h-4 w-4 text-brand-primary border-gray-300 focus:ring-brand-primary rounded"
                />
                <label htmlFor="useMaxRedemptions" className="ml-2 block text-sm font-medium text-gray-700">
                  Set Maximum Redemptions
                </label>
              </div>
              {useMaxRedemptions && (
                <input
                  type="number"
                  id="maxRedemptions"
                  name="maxRedemptions"
                  value={formData.maxRedemptions || ''}
                  onChange={handleChange}
                  min="1"
                  max="10000"
                  placeholder="Max number of times code can be used"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                  required
                />
              )}
            </div>
            
            {/* Discount Percentage - Optional */}
            <div>
              <div className="flex items-center mb-1">
                <input
                  type="checkbox"
                  id="useDiscount"
                  checked={useDiscount}
                  onChange={() => setUseDiscount(!useDiscount)}
                  className="h-4 w-4 text-brand-primary border-gray-300 focus:ring-brand-primary rounded"
                />
                <label htmlFor="useDiscount" className="ml-2 block text-sm font-medium text-gray-700">
                  Set Discount Percentage (for reference)
                </label>
              </div>
              {useDiscount && (
                <div className="relative mt-1 rounded-md shadow-sm">
                  <input
                    type="number"
                    id="discountPercentage"
                    name="discountPercentage"
                    value={formData.discountPercentage || ''}
                    onChange={handleChange}
                    min="1"
                    max="100"
                    placeholder="Discount percentage"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm pr-12"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Description Field - Full width */}
            <div className="col-span-1 md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description (Internal Use)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Enter a description for this promo code (for admin reference only)"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 mr-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Promo Code'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PromoCodeCreationForm;